import { useEffect, useRef, useState } from 'react';
import { DeleteMessageRequest_Scope } from '@gen/chat/v1/methods_p1_pb';
import { useStrings } from '@/i18n/Localization';
import styles from '@/components/DeleteDialog.module.css';

export interface DeleteDialogProps {
  /** False in a conversation where the user may only hide their own copy. */
  canDeleteForEveryone: boolean;
  onConfirm: (scope: DeleteMessageRequest_Scope) => void;
  onCancel: () => void;
}

/**
 * Deletion with an explicit scope.
 *
 * Section 14 requires a delete dialog with an explicit scope and says
 * destructive actions use confirmation dialogs, "not red text alone". So the
 * scope is a choice the user makes deliberately, each option states its
 * consequence, and nothing is deleted by the act of opening this dialog.
 *
 * Focus is trapped for the lifetime of the dialog and returned to the invoking
 * control when it closes, which the caller does by restoring focus after
 * `onCancel` or `onConfirm`.
 */
export function DeleteDialog({ canDeleteForEveryone, onConfirm, onCancel }: DeleteDialogProps) {
  const { t } = useStrings();
  const [scope, setScope] = useState<DeleteMessageRequest_Scope>(DeleteMessageRequest_Scope.SELF);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const initialRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    initialRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }
      // A modal that lets Tab escape behind it is a modal only for people using
      // a mouse. The cycle is computed on each keystroke because the option
      // list changes with `canDeleteForEveryone`.
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, input, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onCancel]);

  return (
    <div className={styles.backdrop}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <h2 id="delete-dialog-title" className={styles.title}>
          {t('delete.title')}
        </h2>
        <p id="delete-dialog-description" className={styles.description}>
          {t('delete.description')}
        </p>

        <fieldset className={styles.options}>
          <legend className={styles.legend}>{t('delete.title')}</legend>

          <label className={styles.option}>
            <input
              ref={initialRef}
              type="radio"
              name="delete-scope"
              checked={scope === DeleteMessageRequest_Scope.SELF}
              onChange={() => {
                setScope(DeleteMessageRequest_Scope.SELF);
              }}
            />
            <span>
              <span className={styles.optionLabel}>{t('delete.scopeSelf')}</span>
              <span className={styles.optionHint}>{t('delete.scopeSelfHint')}</span>
            </span>
          </label>

          {canDeleteForEveryone ? (
            <label className={styles.option}>
              <input
                type="radio"
                name="delete-scope"
                checked={scope === DeleteMessageRequest_Scope.EVERYONE}
                onChange={() => {
                  setScope(DeleteMessageRequest_Scope.EVERYONE);
                }}
              />
              <span>
                <span className={styles.optionLabel}>{t('delete.scopeEveryone')}</span>
                <span className={styles.optionHint}>{t('delete.scopeEveryoneHint')}</span>
              </span>
            </label>
          ) : null}
        </fieldset>

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onCancel}>
            {t('delete.cancel')}
          </button>
          <button
            type="button"
            className={styles.confirm}
            onClick={() => {
              onConfirm(scope);
            }}
          >
            {t('delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
